import { snakeCase } from 'lodash';
import moment from 'moment';
import React, { useMemo } from 'react';
import { Form, FormControl, FormSelect } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { DEFAULT_DATE_TIME_FORMAT_NON_MOMENT, getDateWithTimezone } from '../../../helpers/constants';
import CustomMultiSelect from '../CustomMultiSelect';
import EditableTagsInput from '../EditableTagsInput';
import GrouppedCheckBox from '../GrouppedCheckBox';
import TimeIntervalInWeek from '../TimeIntervalInWeek';
import Title from './Title';
import useLocalization from '../../../hooks/useLocalization';

const getFormControlType = ({ type, isMultiple }) => {
  if (isMultiple) {
    return 'tags-input';
  }

  const typeToFormControlTypeMap = {
    url: 'text',
    currency: 'number',
    datetime: 'datetime-local'
  };

  return typeToFormControlTypeMap[type] || type || 'text';
};

const getFormControlValue = (field, data = {}, formControlType) => {
  const key = field.key;
  const value = data?.[key];
  switch (formControlType) {
    case 'date':
      return value ? moment(value).format('YYYY-MM-DD') : null;
    case 'datetime-local':
      return value ? new Date(value) : null;
    case 'dropdown':
      return value ? value?._id || value : null; // TODO need to handle other cases
    case 'multi-select':
      return value ? value : [];
    case 'select':
      return value !== null && value !== undefined ? value : null;
    case 'radio-checkbox':
      // If isMultiSelect is true, return array; otherwise return single value
      if (field?.isMultiSelect) {
        return Array.isArray(value) ? value : value !== undefined && value !== null ? [value] : [];
      }
      return value !== undefined && value !== null ? value : null;
    case 'number':
    case 'price':
    case 'email':
    case 'text':
    default:
      return value || (field.isMultiple || field?.type === 'groupped-checkbox' ? [] : '');
  }
};

const getNewValue = (field, value, formControlType) => {
  let valueUpdated;
  switch (formControlType) {
    case 'date':
      valueUpdated = value ? getDateWithTimezone(value) : null;
      break;
    default:
      valueUpdated = value;
      break;
  }

  return valueUpdated;
};

const InputCell = ({ data, onChange, field, className, disabled, style }) => {
  const { isRTL, translate } = useLocalization();
  const formControlType = useMemo(() => getFormControlType(field), [field]);

  const options = useMemo(() => {
    return typeof field.options === 'function' ? field.options(data) : field.options || [];
  }, [field.options, data]);

  const handleTagChange = tags => {
    onChange(field, getNewValue(field, tags, formControlType));
  };

  const handleRadioCheckboxChange = (optionValue, checked) => {
    if (field?.isMultiSelect) {
      // Multiple selection mode: add/remove from array
      const currentValue = getFormControlValue(field, data, formControlType);
      const currentArray = Array.isArray(currentValue) ? currentValue : [];

      if (checked) {
        // Add value if not already in array
        if (!currentArray.includes(optionValue)) {
          onChange(field, [...currentArray, optionValue]);
        }
      } else {
        // Remove value from array (toggle off)
        onChange(
          field,
          currentArray.filter(v => v !== optionValue)
        );
      }
    } else {
      // Single selection mode: toggle behavior
      // If checking, set this value (replaces any previous selection)
      // If unchecking, set to null (toggle off)
      onChange(field, checked ? optionValue : null);
    }
  };

  return (
    <div className="">
      {field.type === 'custom-render' && typeof field.render === 'function' ? (
        field.render(data)
      ) : formControlType === 'groupped-checkbox' ? (
        <GrouppedCheckBox
          options={options}
          values={getFormControlValue(field, data, formControlType)}
          onChange={values => onChange(field, values)}
        />
      ) : formControlType === 'checkbox' ? (
        <Form.Check
          label={<h6 className="mb-0 smallFont">{translate(snakeCase(field?.label)) || field?.label}</h6>}
          style={{
            fontSize: '14px'
          }}
          reverse={isRTL}
          checked={getFormControlValue(field, data, formControlType)}
          onChange={e => onChange(field, getNewValue(field, e.target.checked, formControlType))}
          type={formControlType}
          name={`checkbox-${field?.key}`}
          id={`checkbox-${field?.key}`}
        />
      ) : formControlType === 'radio-checkbox' ? (
        <div className="d-flex gap-3 flex-wrap">
          {options?.map(({ value, label }) => {
            const currentValue = getFormControlValue(field, data, formControlType);

            const isChecked = field?.isMultiSelect
              ? Array.isArray(currentValue) && currentValue.includes(value)
              : currentValue === value;

            return (
              <Form.Check
                key={value}
                label={<h6 className="mb-0 smallFont">{translate(snakeCase(label)) || label}</h6>}
                style={{
                  fontSize: '14px'
                }}
                reverse={isRTL}
                checked={isChecked}
                onChange={e => handleRadioCheckboxChange(value, e.target.checked)}
                name={`radio-checkbox-${field?.key}`}
                id={`radio-checkbox-${field?.key}-${value}`}
                type="checkbox"
              />
            );
          })}
        </div>
      ) : formControlType === 'dropdown' ? (
        <FormSelect
          size="sm"
          style={{
            fontSize: '12px'
          }}
          value={getFormControlValue(field, data, formControlType)}
          disabled={disabled}
          onChange={e => {
            let value = e.target.value;
            let newVal =
              value === field?.clearableOptionTitle ||
              value === translate('select_one_ellipsis') ||
              value === 'Select one...'
                ? null
                : value;
            onChange(field, newVal);
          }}
        >
          {field?.clearableOption && (
            <option className="smallFont">
              {field?.clearableOptionTitle || translate('select_one_ellipsis')}
            </option>
          )}
          {options.map(({ value, label }) => (
            <option className="smallFont" key={value} value={value}>
              {label}
            </option>
          ))}
        </FormSelect>
      ) : formControlType === 'grouped-dropdown' ? (
        <CustomMultiSelect
          disabled={disabled}
          items={options}
          selectedItems={getFormControlValue(field, data, formControlType)}
          onChange={option => onChange(field, option)}
          isMulti={field?.isMultiSelect}
          isGroupped
          placeholder={field?.placeholder || translate('select_one')}
          fieldColors={options
            .flatMap(o => o?.options)
            ?.find(option => option?.value === getFormControlValue(field, data, formControlType))}
          closeMenuOnSelect={!field?.isMultiSelect}
        />
      ) : formControlType === 'multi-select' ? (
        <CustomMultiSelect
          disabled={disabled}
          items={options}
          selectedItems={getFormControlValue(field, data, formControlType)}
          onChange={option => onChange(field, option)}
          isMulti
          maxItemCustomMessage={field?.maxItemCustomMessage || undefined}
          placeholder={field?.placeholder || translate('select_one')}
          maxToShow={20}
        />
      ) : formControlType === 'select' ? (
        <CustomMultiSelect
          disabled={disabled}
          items={options}
          selectedItems={(() => {
            const v = getFormControlValue(field, data, formControlType);
            return Array.isArray(v) ? v : v != null && v !== '' ? [v] : [];
          })()}
          onChange={option => onChange(field, option)}
          isMulti={field?.isMultiSelect}
          isGroupped={field?.isGrouped}
          optionRenderer={field?.optionRenderer}
          maxItemCustomMessage={field?.maxItemCustomMessage || undefined}
          placeholder={field?.placeholder || translate('select_one')}
          maxToShow={20}
          closeMenuOnSelect
        />
      ) : formControlType === 'tags-input' ? (
        <>
          <EditableTagsInput
            tags={getFormControlValue(field, data, formControlType)}
            onTagsChange={handleTagChange}
            disabled={disabled}
            fullWidthTag
            autoLowerCase={field?.key === 'emails'}
          />
        </>
      ) : formControlType === 'date' ? (
        <FormControl
          className={`mb-0 text-dark px-2 py-0 ${className}`}
          as={field.as || 'input'}
          style={{
            fontSize: '12px'
          }}
          disabled={disabled}
          type={formControlType}
          value={getFormControlValue(field, data, formControlType)}
          min={typeof field.min === 'function' ? field.min(data) : field.min}
          max={typeof field.max === 'function' ? field.max(data) : field.max}
          onChange={e => onChange(field, getNewValue(field, e.target.value, formControlType))}
          size="sm"
        />
      ) : formControlType === 'followUpDate' ? (
        <div className="">
          <Title field={field} editMode style={{ fontSize: '14px' }} />
          <div className="d-flex my-2">
            <TimeIntervalInWeek
              disabled={disabled}
              className=""
              style={{ marginTop: '-8px' }}
              initialDate={
                // getFormControlValue(field, data, "date") ??
                new Date()
              }
              onChange={newDate => onChange(field, newDate)}
            />
          </div>
          <DatePicker
            wrapperClassName="w-100"
            className={`${isRTL ? 'px-4' : ''}   mb-0 text-dark px-2 py-1 form-control form-control-sm ${className}`}
            selected={getFormControlValue(field, data, 'datetime-local')}
            dateFormat={DEFAULT_DATE_TIME_FORMAT_NON_MOMENT}
            onChange={e => {
              onChange(field, e ? getNewValue(field, e.toISOString(), 'datetime-local') : null);
            }}
            openToDate={new Date()} // Opens the datepicker to the current month and year
            showYearDropdown
            showMonthDropdown
            showTimeInput
            dropdownMode="scroll"
            timeFormat="HH:mm" // 24-hour format
            popperClassName="datepickerPopperClassName"
            style={style}
            disabled={disabled}
            clearButtonClassName="text-dark"
            portalId="root"
            isClearable
          />
        </div>
      ) : (
        <FormControl
          className={`mb-0 text-dark px-2 py-0 ${className}`}
          as={field.as || 'input'}
          style={{
            fontSize: '12px'
          }}
          disabled={disabled}
          type={formControlType}
          value={getFormControlValue(field, data, formControlType)}
          onChange={e => onChange(field, getNewValue(field, e.target.value, formControlType))}
          size="sm"
        />
      )}
    </div>
  );
};

export default InputCell;
